import { useEffect, useState, useMemo, useCallback } from 'react';
import { X, Type, RotateCcw } from 'lucide-react';
import { loadSubtitleFont } from '@/lib/fontLoader';
import { mergeSubtitleStyle, generateSubtitlePreview, type WrapResult } from '@/lib/subtitleUtils';
import { FontSettings } from './subtitle/FontSettings';
import { ColorSettings } from './subtitle/ColorSettings';
import type { Scene, SubtitleStyle, SubtitleSettings } from '@/types';

interface SceneSubtitleStyleModalProps {
  open: boolean;
  scene: Scene | null;
  subtitleSettings: SubtitleSettings;
  onClose: () => void;
  onSave: (sceneId: string, style: Partial<SubtitleStyle>) => void;
  onReset: (sceneId: string) => void;
}

export function SceneSubtitleStyleModal({
  open,
  scene,
  subtitleSettings,
  onClose,
  onSave,
  onReset,
}: SceneSubtitleStyleModalProps) {
  // ローカル編集状態（保存するまでStoreには反映しない）
  const [localStyle, setLocalStyle] = useState<Partial<SubtitleStyle>>({});
  const [fontLoaded, setFontLoaded] = useState(false);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [wrapResult, setWrapResult] = useState<WrapResult | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  // シーンが変更されたらローカル状態をリセット
  useEffect(() => {
    if (scene) {
      setLocalStyle(scene.subtitleStyle || {});
    }
    // scene.idが変わった時のみリセットしたいため、sceneオブジェクト全体は依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.id]);

  // 画像サイズを取得
  useEffect(() => {
    if (!scene?.imageUrl) {
      setImageSize(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = scene.imageUrl;
  }, [scene?.imageUrl]);

  // マージしたスタイル（デフォルト + ローカル編集）
  const mergedStyle = useMemo(() => {
    return mergeSubtitleStyle(subtitleSettings.defaultStyle, localStyle);
  }, [subtitleSettings.defaultStyle, localStyle]);

  // フォント変更時にロード
  useEffect(() => {
    if (!open) return;

    // 非同期処理として実行（リントエラー回避）
    Promise.resolve().then(() => setFontLoaded(false));

    loadSubtitleFont(mergedStyle.fontFamily).then(() => {
      setFontLoaded(true);
    }).catch(() => {
      setFontLoaded(true);
    });
  }, [open, mergedStyle.fontFamily]);

  // プレビュー生成（Canvas使用、動画生成と同じロジック）
  const generatePreview = useCallback(async () => {
    if (!scene?.imageUrl || !fontLoaded || !imageSize) return;

    setIsGeneratingPreview(true);
    try {
      const result = await generateSubtitlePreview(
        scene.imageUrl,
        scene.subtitleText || '',
        subtitleSettings,
        mergedStyle,
        imageSize.width,
        imageSize.height
      );
      setPreviewDataUrl(result.dataUrl);
      setWrapResult(result.wrapResult);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  }, [scene?.imageUrl, scene?.subtitleText, subtitleSettings, mergedStyle, fontLoaded, imageSize]);

  // スタイル変更時にプレビューを再生成
  useEffect(() => {
    if (open && fontLoaded) {
      generatePreview();
    }
  }, [open, fontLoaded, generatePreview]);

  if (!open || !scene) return null;

  const updateLocalStyle = (updates: Partial<SubtitleStyle>) => {
    setLocalStyle((prev) => ({ ...prev, ...updates }));
  };

  const handleSave = () => {
    onSave(scene.id, localStyle);
    onClose();
  };

  const handleReset = () => {
    setLocalStyle({});
    onReset(scene.id);
  };

  const handleCancel = () => {
    // ローカル状態を元に戻す
    setLocalStyle(scene.subtitleStyle || {});
    onClose();
  };

  const hasChanges = Object.keys(localStyle).length > 0;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div
        className="bg-white rounded-2xl shadow-xl w-[calc(100%-2rem)] md:w-[800px] max-w-[95vw] max-h-[calc(100dvh-2rem)] md:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header shrink-0">
          <h2 className="flex items-center gap-2">
            <Type size={20} className="text-coral-500" />
            シーン {scene.order + 1} のテロップスタイル
          </h2>
          <button onClick={handleCancel} className="btn-icon" aria-label="閉じる">
            <X size={20} />
          </button>
        </div>

        {/* Body - Side by Side Layout */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
          {/* Left: Preview with Scene Image */}
          <div className="w-full md:w-[380px] shrink-0 p-4 md:p-6 bg-warm-50 border-b md:border-b-0 md:border-r border-warm-200 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Type size={16} className="text-warm-600" />
                <h3 className="font-semibold text-warm-800 text-sm">プレビュー</h3>
              </div>
              {(isGeneratingPreview || !fontLoaded) && (
                <span className="text-xs text-warm-400">読み込み中...</span>
              )}
            </div>

            {/* Canvas-based preview (動画生成と同じロジック) */}
            <div className="flex-1 min-h-40 md:min-h-60 flex items-center justify-center rounded-lg overflow-hidden" style={{ backgroundColor: '#CCCCCC' }}>
              {previewDataUrl ? (
                <img
                  src={previewDataUrl}
                  alt={`Scene ${scene.order + 1} preview`}
                  className="block max-w-full max-h-[160px] md:max-h-[300px] object-contain"
                  style={{ opacity: isGeneratingPreview ? 0.5 : 1, transition: 'opacity 0.2s' }}
                />
              ) : (
                <div className="text-warm-400 text-sm">プレビューを生成中...</div>
              )}
            </div>

            {!scene.subtitleText && (
              <p className="text-xs text-amber-600 mt-2 text-center">
                テロップテキストが未設定です
              </p>
            )}

            {wrapResult?.exceedsMaxLines && (
              <p className="text-xs text-red-600 mt-2 text-center font-medium">
                ⚠️ 2行を超えています（3行目以降は切り捨て）
              </p>
            )}

            {hasChanges && (
              <p className="text-xs text-coral-600 mt-2 text-center">
                ※ 未保存の変更があります
              </p>
            )}
          </div>

          {/* Right: Style Settings */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 md:p-6 space-y-5">
            {/* Font Settings */}
            <FontSettings style={mergedStyle} onUpdate={updateLocalStyle} />

            <div className="divider" />

            {/* Color Settings */}
            <ColorSettings style={mergedStyle} onUpdate={updateLocalStyle} />

            <div className="divider" />

            {/* Reset Button */}
            <div className="flex justify-center">
              <button
                onClick={handleReset}
                className="btn-ghost text-warm-500 text-sm flex items-center gap-1"
                disabled={!hasChanges && !scene.subtitleStyle}
              >
                <RotateCcw size={14} />
                デフォルトに戻す
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer shrink-0 flex flex-wrap gap-3">
          <button onClick={handleCancel} className="btn-ghost flex-1 min-w-[8rem]">
            キャンセル
          </button>
          <button onClick={handleSave} className="btn-primary flex-1 min-w-[8rem]">
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
